/*
Description: This file handles interacting with the Kinser/WellSky EMR.

Notes:
    All injectors need the following functions:
        getPageInfo
        getPatientInfo
        getQuestions
        answerQuestions
        answerQuestion
*/

//  ---------------------------- INITIALIZATION ----------------------------
/**
 * Kinser/WellSky (kinnser.net) renders inside a fixed-width Bootstrap `.container`
 * (#AMContainer) that does not reflow when the viewport shrinks. When the
 * Comprehend side panel is open the EMR viewport narrows and the right edge gets
 * cut off. This scales the whole page down (CSS `zoom` on <html>, the same effect
 * as Cmd+-) just enough to fit the available width, and restores 100% when there
 * is room again.
 *
 * Zoom is applied to the document root (not #AMContainer) so JS-positioned overlays
 * appended to <body> (tooltips, jQuery UI dialogs, popovers, datepickers) scale
 * with the page and stay aligned.
 */

const CONTENT_SELECTOR = '#AMContainer';

function isKinser(): boolean {
  const host = window.location.hostname;
  return host === 'kinnser.net' || host.endsWith('.kinnser.net');
}

function applyFit(): void {
  const root = document.documentElement;

  // Reset first so we measure the page's natural (unzoomed) width. Reading
  // scrollWidth forces a synchronous reflow; because the final zoom is set before
  // this function returns, only the final state is painted (no flicker).
  root.style.removeProperty('zoom');

  const content = document.querySelector(
    CONTENT_SELECTOR
  ) as HTMLElement | null;
  const naturalWidth = content?.scrollWidth || root.scrollWidth;
  const viewportWidth = root.clientWidth;
  if (!naturalWidth || !viewportWidth) return;

  // viewportWidth = naturalWidth * zoom
  const targetZoom = viewportWidth / naturalWidth;
  if (targetZoom >= 1) return; // Don't zoom up, only down.

  root.style.setProperty('zoom', String(targetZoom));
}

let scheduled = false;

function scheduleFit(): void {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    applyFit();
  });
}

export function initKinserViewportFit(): void {
  if (!isKinser()) return;

  // The side panel opening/resizing/closing all fire resize
  window.addEventListener('resize', scheduleFit);
  // also handle if user reloads/navigates
  window.addEventListener('load', scheduleFit, { once: true });

  scheduleFit();
}

//  ---------------------------- CONSTANTS/STATE ----------------------------

const DEFAULT_DOB = '05/29/2023';
const DEFAULT_NOTE_TYPE = 'Default Note';
const KINSER_AUTO_SAVE_DELAY_MS = 5000;
const FREE_TEXT_WRITER_PATTERN = /\|spWritefrmVarchar(?:Max)?\|/i;
const NUMERIC_TEXT_FIELD_SECTIONS = new Set(['Vital Signs']);
const NUMERIC_TEXT_FIELD_UNIT_PATTERN =
  /\((ft|in|cm|mm|m|lbs?|kg|sec|secs?|min|mins?|%|deg|degrees?|reps?)\)\s*$/i;
const SLASH_PAIR_DESCRIPTION =
  'Enter both numbers as one value separated by a slash, e.g. 120/80.';
const MAX_STANDALONE_FIELD_LABEL_WORDS = 12;
const FIELD_ID_WORD_LABELS = {
  asmt: 'Assessment',
  bpdia: 'BP Diastolic',
  bpsys: 'BP Systolic',
  circum: 'Circumferential',
  conf: 'Conference',
  coord: 'Coordination',
  desc: 'Description',
  loc: 'Location',
  msrmnt: 'Measurement',
  oth: 'Other',
  phy: 'Physical',
  prim: 'Primary',
  resp: 'Respirations',
  sec: 'Secondary',
  si: 'Skilled Intervention',
  trtmnt: 'Treatment',
  txt: '',
  text: '',
  vs: ''
};
const WEAK_FIELD_LABEL_WORDS = new Set([
  'details',
  'location',
  'other',
  'primary',
  'secondary',
  'site'
]);

var questions = [];
var questionInteractions = {};
let kinserAutoSaveTimer = null;
let activeKinserAnswerCount = 0;

//  ---------------------------- UTILITY FUNCTIONS  ----------------------------

// Question keys are user-facing labels, so normalize punctuation/spacing before
// using labels as object keys.
function sanitizeKey(key) {
  return key
    .replace(/[:?"']/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function splitPatientName(patientName) {
  if (!patientName) return { firstName: '', lastName: '' };

  const cleanName = patientName.replace(/\([^)]*\)/g, '').trim();
  if (cleanName.includes(',')) {
    const [lastName, firstName] = cleanName.split(',', 2);
    return {
      firstName: firstName?.trim() || '',
      lastName: lastName?.trim() || ''
    };
  }

  const parts = cleanName.split(/\s+/).filter(Boolean);
  return {
    firstName: parts[0] || '',
    lastName: parts.length > 1 ? parts[parts.length - 1] : ''
  };
}

function isEditableField(field) {
  return field.getAttribute('contenteditable') === 'true';
}

function isEmptyOrNullorSameAsExisting(aiResponse, existingValue) {
  return (
    aiResponse === null ||
    aiResponse === undefined ||
    aiResponse === '' ||
    aiResponse === 'None' ||
    aiResponse === existingValue
  );
}

// Append mode keeps the current EMR value and adds only new generated text.
// If the model echoes the current value at the start of its response, strip
// that echoed prefix before appending so the existing note is not duplicated.
function stripExistingPrefixForAppend(value, existingValue) {
  if (typeof value !== 'string' || typeof existingValue !== 'string') {
    return value;
  }

  const existingText = existingValue.trim();
  const responseText = value.trim();

  if (
    !existingText ||
    !responseText ||
    !responseText.startsWith(existingText)
  ) {
    return value;
  }

  return responseText
    .slice(existingText.length)
    .replace(/^[\s\r\n:;.,-]+/, '')
    .trim();
}

function getFieldLogInfo(field) {
  return {
    id: field?.id || '',
    name: field?.getAttribute?.('name') || '',
    tagName: field?.tagName || ''
  };
}

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function normalizeFlexKey(key) {
  return key.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function findFlexKey(container, key) {
  if (key in container) return key;

  const normalizedKey = normalizeFlexKey(key);
  return Object.keys(container).find(
    (containerKey) => normalizeFlexKey(containerKey) === normalizedKey
  );
}

function getFlexFieldAlias(key) {
  const aliases = {
    briefdescriptionoffunctionalimpact: 'Description of Functional Impact'
  };

  return aliases[normalizeFlexKey(key)] || key;
}

function getSectionHeaderForElement(element) {
  const documentOrderSection = getSectionHeaderElementByDocumentOrder(element);
  if (documentOrderSection) {
    return sanitizeKey(documentOrderSection.textContent || '');
  }

  let current = element;

  // Kinser wraps each visible section as:
  // .section-hdr followed by a sibling content container. Walk up from the
  // field until we find that preceding header
  while (current && current !== document.body) {
    let sibling = current.previousElementSibling;
    while (sibling) {
      if (sibling.classList?.contains('section-hdr')) {
        return sanitizeKey(sibling.textContent || '');
      }
      sibling = sibling.previousElementSibling;
    }

    current = current.parentElement;
  }

  return '';
}

function getDuplicateSectionSubsectionName(element, sectionName) {
  const header = getSectionHeaderElementByDocumentOrder(element);
  if (!header || !sectionName) return '';

  const headerName = sanitizeKey(header.textContent || '');
  if (headerName !== sectionName) return '';

  const matchingHeaders = Array.from(
    (document.body || document).querySelectorAll('.section-hdr')
  ).filter(
    (candidate) => sanitizeKey(candidate.textContent || '') === sectionName
  );
  if (matchingHeaders.length <= 1) return '';

  const duplicateIndex = matchingHeaders.indexOf(header);
  if (duplicateIndex <= 0) return '';

  return getDuplicateSectionQualifier(header, element);
}

function getDuplicateSectionQualifier(header, element) {
  const content = header.nextElementSibling;
  if (!content) return '';

  const candidates = Array.from(
    content.querySelectorAll('h4, .sub-header, .subHeader, .subHeaderTxt')
  );
  const precedingCandidates = candidates.filter(
    (candidate) =>
      candidate.compareDocumentPosition(element) &
      Node.DOCUMENT_POSITION_FOLLOWING
  );
  const preferredCandidates =
    precedingCandidates.length > 0 ? precedingCandidates : candidates;

  return (
    preferredCandidates
      .map((candidate) => cleanFieldLabel(candidate.textContent || ''))
      .find(
        (label) =>
          isUsefulLabel(label) &&
          getLabelWords(label).length <= MAX_STANDALONE_FIELD_LABEL_WORDS &&
          !['level', 'functional impact'].includes(label.toLowerCase())
      ) || ''
  );
}

function getSectionHeaderElementByDocumentOrder(element) {
  const root = document.body || document;
  const precedingHeaders = Array.from(
    root.querySelectorAll('.section-hdr')
  ).filter(
    (header) =>
      header.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING
  );

  return precedingHeaders[precedingHeaders.length - 1] || null;
}

function getFreeTextSectionName(field) {
  return getSectionHeaderForElement(field);
}

function cleanLabelText(text) {
  return (text || '')
    .replace(/\u00a0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function cleanFieldLabel(text) {
  return cleanLabelText(text)
    .replace(/[:：]+$/g, '')
    .trim();
}

function isUsefulLabel(text) {
  const cleanText = cleanFieldLabel(text);
  if (!cleanText) return false;
  if (!/[a-z0-9]/i.test(cleanText)) return false;

  return !['x', 'clear'].includes(cleanText.toLowerCase());
}

function getElementTextWithoutControls(element) {
  if (!element) return '';

  const clone = element.cloneNode(true);
  clone.querySelectorAll('select').forEach((select) => {
    const selectedOption =
      Array.from(select.options).find((option) => option.selected) ||
      select.options[select.selectedIndex];
    const selectedText = cleanLabelText(selectedOption?.textContent || '');

    if (selectedText) {
      select.replaceWith(
        element.ownerDocument.createTextNode(` ${selectedText} `)
      );
      return;
    }

    select.remove();
  });
  clone
    .querySelectorAll('input, textarea, label, script, style')
    .forEach((node) => node.remove());
  return cleanLabelText(clone.textContent || '');
}

function getTextBeforeField(field, container) {
  if (!field || !container || !container.contains(field)) return '';

  try {
    const range = document.createRange();
    range.setStart(container, 0);
    range.setEndBefore(field);

    const clone = range.cloneContents();
    clone
      .querySelectorAll?.('input, select, textarea, script, style')
      .forEach((node) => node.remove());
    return cleanLabelText(clone.textContent || '');
  } catch (error) {
    return '';
  }
}

function getLabelFromTextBeforeField(field, container) {
  const text = getTextBeforeField(field, container);
  if (!text) return '';

  const colonMatch = text.match(/([^:]+):\s*$/);
  if (colonMatch) {
    return cleanFieldLabel(colonMatch[1]);
  }

  const fragments = text
    .split(/\s{2,}|[\n\r\t]+/)
    .map(cleanFieldLabel)
    .filter(isUsefulLabel);

  return fragments[fragments.length - 1] || '';
}

function isVisibleField(field) {
  const hasLayout = Boolean(
    field.offsetParent || field.getClientRects().length > 0
  );
  if (!hasLayout) return false;

  let current = field;
  while (current) {
    if (window.getComputedStyle(current).visibility === 'hidden') {
      return false;
    }
    current = current.parentElement;
  }

  return true;
}

function isTextControl(field) {
  const tagName = field.tagName?.toLowerCase();
  const fieldType = (field.getAttribute('type') || 'text').toLowerCase();

  if (tagName !== 'textarea' && tagName !== 'input') return false;
  if (
    tagName === 'input' &&
    !['text', 'search', 'tel', 'number'].includes(fieldType)
  ) {
    return false;
  }

  return true;
}

function isWritableField(field) {
  return !field.disabled && !field.readOnly;
}

function isKinserPersistedTextField(field) {
  // Kinser encodes persisted varchar note fields in the control name.
  return FREE_TEXT_WRITER_PATTERN.test(field.getAttribute('name') || '');
}

function isFreeTextField(field) {
  return (
    isKinserPersistedTextField(field) &&
    isVisibleField(field) &&
    isWritableField(field) &&
    isTextControl(field)
  );
}

function getCanonicalFieldName(field) {
  const fieldName = field.getAttribute('name') || '';
  return cleanLabelText(fieldName.split('|')[0] || field.id || '');
}

function getConfiguredMaxLength(field) {
  const maxLength = Number(field.getAttribute('maxlength'));
  return Number.isInteger(maxLength) && maxLength > 0 ? maxLength : null;
}

function getNearestSubHeader(element) {
  if (!element) return '';

  const root = document.body || document;
  const sectionHeader = getSectionHeaderElementByDocumentOrder(element);
  const precedingSubHeaders = Array.from(
    root.querySelectorAll('.sub-header, .subHeader')
  ).filter((header) => {
    const isBeforeElement =
      header.compareDocumentPosition(element) &
      Node.DOCUMENT_POSITION_FOLLOWING;
    if (!isBeforeElement) return false;

    return (
      !sectionHeader ||
      sectionHeader.compareDocumentPosition(header) &
        Node.DOCUMENT_POSITION_FOLLOWING
    );
  });

  return cleanLabelText(
    precedingSubHeaders[precedingSubHeaders.length - 1]?.textContent || ''
  );
}

function getFreeTextSubsectionName(field, sectionName) {
  const duplicateSectionSubsectionName = getDuplicateSectionSubsectionName(
    field,
    sectionName
  );
  if (duplicateSectionSubsectionName) return duplicateSectionSubsectionName;

  const subsectionName = getNearestSubHeader(field);
  if (subsectionName && subsectionName !== sectionName) return subsectionName;

  return '';
}

function getTableColumnHeaderParts(table, row, cellIndex) {
  const rows = Array.from(table.rows);
  const rowIndex = rows.indexOf(row);
  const parts = [];

  rows.slice(0, rowIndex).forEach((headerRow) => {
    let cursor = 0;
    Array.from(headerRow.cells).some((cell) => {
      const colSpan = cell.colSpan || 1;
      const coversColumn = cursor <= cellIndex && cellIndex < cursor + colSpan;
      cursor += colSpan;

      if (!coversColumn) return false;

      const text = getElementTextWithoutControls(cell);
      if (text && !parts.includes(text)) parts.push(text);
      return true;
    });
  });

  return parts;
}

function getCarryForwardCellLabel(table, row, cellIndex) {
  const rows = Array.from(table.rows);
  const rowIndex = rows.indexOf(row);

  for (let i = rowIndex - 1; i >= 0; i -= 1) {
    const cell = rows[i].cells[cellIndex];
    if (!cell || cell.colSpan > 1) continue;

    const label = cleanFieldLabel(getElementTextWithoutControls(cell));
    if (
      isUsefulLabel(label) &&
      !['part', 'action'].includes(label.toLowerCase())
    ) {
      return label;
    }
  }

  return '';
}

function getTableRowPartLabel(table, row, cellIndex) {
  const cell = row.cells[cellIndex];
  const label = cleanFieldLabel(getElementTextWithoutControls(cell));
  if (isUsefulLabel(label)) return label;
  if (cellIndex !== 0) return '';

  return getCarryForwardCellLabel(table, row, cellIndex);
}

function getNearestRowHeadingLabel(row, cellIndex) {
  const cells = Array.from(row.cells).slice(0, cellIndex).reverse();

  for (const cell of cells) {
    const heading = Array.from(cell.querySelectorAll('h4'))
      .map((element) => cleanFieldLabel(element.textContent || ''))
      .find(isUsefulLabel);
    if (heading) return heading;
  }

  return '';
}

function getTableFieldLabel(field) {
  const table = field.closest('table');
  const row = field.closest('tr');
  const cell = field.closest('td, th');
  if (!table || !row || !cell) return '';

  const cells = Array.from(row.cells);
  const cellIndex = cells.indexOf(cell);
  const textBeforeField = getLabelFromTextBeforeField(field, cell);
  const nearestRowHeading = getNearestRowHeadingLabel(row, cellIndex);
  const columnParts = getTableColumnHeaderParts(table, row, cellIndex);

  if (nearestRowHeading) {
    const headingParts = [
      nearestRowHeading,
      ...columnParts.map(cleanFieldLabel)
    ];
    const uniqueHeadingParts = [];

    headingParts.filter(isUsefulLabel).forEach((part) => {
      if (!uniqueHeadingParts.includes(part)) uniqueHeadingParts.push(part);
    });

    return cleanFieldLabel(uniqueHeadingParts.join(' - '));
  }

  const rowParts = cells
    .slice(0, cellIndex)
    .map((_, index) => getTableRowPartLabel(table, row, index))
    .filter(isUsefulLabel);
  const tableParts = [];

  [...rowParts, ...columnParts.map(cleanFieldLabel)]
    .filter(isUsefulLabel)
    .forEach((part) => {
      if (!tableParts.includes(part)) tableParts.push(part);
    });

  if (textBeforeField && tableParts.length === 0) {
    return textBeforeField;
  }

  if (textBeforeField && !tableParts.includes(textBeforeField)) {
    tableParts.push(textBeforeField);
  }

  return cleanFieldLabel(tableParts.join(' - '));
}

function getLabelWords(label) {
  return cleanFieldLabel(label)
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean);
}

function isWeakFieldLabel(label) {
  const words = getLabelWords(label);
  if (words.length === 0) return false;

  return words.every((word) => WEAK_FIELD_LABEL_WORDS.has(word));
}

function getSpecificLabelWordCount(label) {
  return getLabelWords(label).filter(
    (word) => !WEAK_FIELD_LABEL_WORDS.has(word) && /[a-z]/i.test(word)
  ).length;
}

function expandFieldIdWord(word) {
  return FIELD_ID_WORD_LABELS[word.toLowerCase()] ?? word;
}

function normalizeFieldIdWords(words) {
  const lastWord = words[words.length - 1];
  if (['Primary', 'Secondary'].includes(lastWord) && words.length > 2) {
    return [lastWord, ...words.slice(0, -1)];
  }

  return words;
}

function formatFieldIdLabel(label) {
  let formattedLabel = cleanFieldLabel(label).replace(
    /^(Primary|Secondary) (.+) Assessment Site Other Description$/,
    '$1 $2 Site - Other Description'
  );

  if (!/Other Description$/i.test(formattedLabel)) {
    formattedLabel = formattedLabel.replace(/\s+Other$/, ' - Other');
  }

  return formattedLabel;
}

function trimContextPrefix(label, contextLabel) {
  const labelWords = cleanFieldLabel(label)
    .replace(/[^a-z0-9]+/gi, ' ')
    .split(/\s+/)
    .filter(Boolean);
  const contextWords = cleanFieldLabel(contextLabel)
    .replace(/[^a-z0-9]+/gi, ' ')
    .split(/\s+/)
    .filter((word) => word && word.toLowerCase() !== 'this');

  if (contextWords.length === 0 || labelWords.length <= contextWords.length) {
    return label;
  }

  const hasPrefix = contextWords.every(
    (word, index) => word.toLowerCase() === labelWords[index]?.toLowerCase()
  );
  if (!hasPrefix) return label;

  const trimmedLabel = labelWords.slice(contextWords.length).join(' ');
  if (
    !trimmedLabel ||
    !/[a-z]/i.test(trimmedLabel) ||
    isWeakFieldLabel(trimmedLabel)
  ) {
    return label;
  }

  return trimmedLabel;
}

function getFieldIdLabel(field, subsectionName = '') {
  const rawKey = field.id || field.getAttribute('name') || 'Free Text';
  const fieldIdWords = rawKey
    .split('|')[0]
    .replace(/^frm_?/, '')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([a-zA-Z])([0-9])/g, '$1 $2')
    .replace(/([0-9])([a-zA-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .split(/\s+/)
    .map(expandFieldIdWord)
    .filter(Boolean);
  const expandedLabel = cleanLabelText(
    normalizeFieldIdWords(fieldIdWords).join(' ')
  );

  const contextualLabel = [
    getFreeTextSectionName(field),
    subsectionName
  ].reduce(
    (label, contextLabel) => trimContextPrefix(label, contextLabel),
    expandedLabel
  );

  return formatFieldIdLabel(contextualLabel);
}

function humanizeFieldId(field, subsectionName = '') {
  const label = getFieldIdLabel(field, subsectionName);

  return cleanLabelText(label || 'Free Text');
}

function chooseFreeTextLabel(label, fieldIdLabel) {
  const labelWords = getLabelWords(label);
  const fieldIdWords = getLabelWords(fieldIdLabel);
  const hasWeakLabelWord = labelWords.some((word) =>
    WEAK_FIELD_LABEL_WORDS.has(word)
  );
  const fieldIdLabelLooksNumeric = /\d/.test(fieldIdLabel);

  if (
    !fieldIdLabelLooksNumeric &&
    isWeakFieldLabel(label) &&
    fieldIdWords.length > labelWords.length
  ) {
    return fieldIdLabel;
  }

  if (
    !fieldIdLabelLooksNumeric &&
    hasWeakLabelWord &&
    getSpecificLabelWordCount(fieldIdLabel) > getSpecificLabelWordCount(label)
  ) {
    return fieldIdLabel;
  }

  return label;
}

function getExplicitLabelForField(field) {
  if (!field.id) return null;

  try {
    const escapedId = window.CSS?.escape
      ? window.CSS.escape(field.id)
      : field.id.replace(/"/g, '\\"');
    const label = document.querySelector(`label[for="${escapedId}"]`);
    if (label) return label;
  } catch (error) {
    // Fall back to matching label.htmlFor below.
  }

  return Array.from(document.querySelectorAll('label')).find(
    (label) => label.htmlFor === field.id
  );
}

function getNearestStandaloneFieldLabel(field) {
  const candidates = [
    field.parentElement,
    field.closest('td'),
    field.closest('.moduleCol'),
    field.closest('div'),
    field.closest('.moduleRow')
  ];

  for (const container of candidates) {
    const label = getLabelFromTextBeforeField(field, container);
    if (
      isUsefulLabel(label) &&
      getLabelWords(label).length <= MAX_STANDALONE_FIELD_LABEL_WORDS
    ) {
      return label;
    }
  }

  return '';
}

function getFallbackFieldLabel(field, subsectionName = '') {
  if (subsectionName && !['Comments'].includes(subsectionName)) {
    return `${subsectionName} Details`;
  }

  return humanizeFieldId(field, subsectionName);
}

function getFreeTextFieldLabel(field, subsectionName = '') {
  const fieldIdLabel = humanizeFieldId(field, subsectionName);

  const commentsTable = field.closest('.commentsTable');
  if (
    commentsTable?.textContent?.includes(
      'Factors Contributing to Functional Impairment'
    )
  ) {
    return 'Factors Contributing to Functional Impairment';
  }

  const tableLabel = getTableFieldLabel(field);
  if (tableLabel) return chooseFreeTextLabel(tableLabel, fieldIdLabel);

  const explicitLabel = getExplicitLabelForField(field);
  if (explicitLabel) {
    return chooseFreeTextLabel(
      cleanFieldLabel(explicitLabel.textContent),
      fieldIdLabel
    );
  }

  const standaloneLabel = getNearestStandaloneFieldLabel(field);
  if (standaloneLabel)
    return chooseFreeTextLabel(standaloneLabel, fieldIdLabel);

  return getFallbackFieldLabel(field, subsectionName);
}

function isRomStrengthField(field) {
  const sectionName = getFreeTextSectionName(field);
  const subsectionName = getFreeTextSubsectionName(field, sectionName);

  return (
    sectionName === 'Physical Assessment' && subsectionName === 'ROM / Strength'
  );
}

function isRomStrengthGridField(field) {
  const table = field.closest('table');
  if (!table || !isRomStrengthField(field)) return false;

  const tableLabel = cleanLabelText(getElementTextWithoutControls(table));
  return /\bROM\b/i.test(tableLabel) && /\bStrength\b/i.test(tableLabel);
}

function getFreeTextFieldDescription(field, label) {
  if (field.id === 'frm_ROMComments') {
    return 'Briefly summarize the functional impact for the ROM / Strength section. Keep this to 1-2 concise sentences and under 200 characters.';
  }

  const maxLength = getConfiguredMaxLength(field);
  if (isRomStrengthField(field)) {
    return `ROM/strength for ${label}. Use source-supported value only; NT if not tested; None if unspecified. Max ${maxLength} chars.`;
  }

  if (field.tagName?.toLowerCase() !== 'textarea' && maxLength) {
    return `Free-text input for ${label}. Maximum length: ${maxLength} characters.`;
  }

  if (maxLength && maxLength <= 200) {
    return `Keep this concise. Maximum length: ${maxLength} characters.`;
  }

  return `Free-text field for ${label}.`;
}

function getUniqueFieldKey(structure, label, field) {
  let key = sanitizeKey(label) || humanizeFieldId(field);
  if (!structure[key]) return key;

  const idSuffix = sanitizeKey(field.id || field.getAttribute('name') || '');
  if (idSuffix && !structure[`${key} - ${idSuffix}`]) {
    return `${key} - ${idSuffix}`;
  }

  let count = 2;
  while (structure[`${key} (${count})`]) count += 1;
  return `${key} (${count})`;
}

function getUniqueFreeTextFieldGroups(root) {
  const fieldGroups = new Map();

  Array.from(root.querySelectorAll('input, textarea'))
    .filter(isFreeTextField)
    .forEach((field) => {
      const fieldKey = getCanonicalFieldName(field);
      if (!fieldKey) return;

      if (!fieldGroups.has(fieldKey)) {
        fieldGroups.set(fieldKey, {
          primaryField: field,
          fields: []
        });
      }

      fieldGroups.get(fieldKey).fields.push(field);
    });

  return Array.from(fieldGroups.values());
}

function registerTextOnlyFlexQuestions() {
  const root = document.body;
  const sectionStructures = {};
  const sectionOriginalElements = {};
  // Right-hand members of a slash pair are folded into the left member's
  // combined field, so skip them when their own group comes up.
  const slashPairPartnerNames = new Set();

  getUniqueFreeTextFieldGroups(root).forEach(
    ({ primaryField: field, fields }) => {
      if (slashPairPartnerNames.has(getCanonicalFieldName(field))) {
        return;
      }

      const sectionName = getFreeTextSectionName(field);

      if (!sectionName) {
        return;
      }

      const structure = sectionStructures[sectionName] || {};
      const subsectionName = getFreeTextSubsectionName(field, sectionName);
      const label = getFreeTextFieldLabel(field, subsectionName);
      const targetKey = subsectionName || sectionName;
      const targetStructure = structure[targetKey] || {};
      const fieldKey = getUniqueFieldKey(targetStructure, label, field);

      const slashPartner = getSlashPairPartner(field);
      if (slashPartner) {
        slashPairPartnerNames.add(getCanonicalFieldName(slashPartner));
        targetStructure[fieldKey] = buildSlashPairField(
          field,
          slashPartner,
          SLASH_PAIR_DESCRIPTION
        );
      } else {
        targetStructure[fieldKey] = buildFlexTextField(
          fields,
          label,
          getFreeTextFieldDescription(field, label)
        );
      }

      structure[targetKey] = targetStructure;

      sectionStructures[sectionName] = structure;
      if (!sectionOriginalElements[sectionName]) {
        sectionOriginalElements[sectionName] = field;
      }
    }
  );

  Object.entries(sectionStructures).forEach(([sectionName, structure]) => {
    registerStructureFlexQuestion(
      sectionName,
      structure,
      sectionOriginalElements[sectionName]
    );
  });
}

function getFieldList(fields) {
  return (Array.isArray(fields) ? fields : [fields]).filter(Boolean);
}

function setFieldListValue(fields, value, replyType) {
  getFieldList(fields).forEach((field) => {
    const existingValue = getFieldValue(field);

    if (isEmptyOrNullorSameAsExisting(value, existingValue)) {
      return;
    }

    setFieldValue(field, value, replyType);
  });
}

function buildFlexTextField(fields, label = '', description = '') {
  const fieldList = getFieldList(fields);
  const primaryField = fieldList[0];

  // The serializable part exposes the current EMR text as "Existing Value".
  // The Input function is kept in questionInteractions and used only when
  // PUSH_ANSWER runs inside this page.
  const flexField = {
    'Existing Value': getFieldValue(primaryField),
    Input: async (value, replyType) => {
      setFieldListValue(fieldList, value, replyType);
    }
  };

  if (description) {
    flexField.Description = description;
  }

  return flexField;
}

// Two text inputs joined by a cell whose only content is "/" (e.g. blood
// pressure systolic / diastolic) read as one "X/Y" value. The model fills the
// combined field naturally; here we split on "/" and write each box. Detection
// keys on the visible separator, not field ids, so it survives EMR id changes.
function getSlashPairPartner(field) {
  const separator = field.closest('td')?.nextElementSibling;
  if (!separator) return null;
  if (cleanLabelText(getElementTextWithoutControls(separator)) !== '/') {
    return null;
  }

  const partner =
    separator.nextElementSibling?.querySelector('input, textarea');
  if (!partner || !isFreeTextField(partner)) return null;

  return partner;
}

function buildSlashPairField(leftField, rightField, description = '') {
  const leftValue = getFieldValue(leftField);
  const rightValue = getFieldValue(rightField);

  // Split on "/" and write each box separately. normalizeValueForField strips
  // any trailing qualifier (e.g. "78 prior" -> "78") on write, so we don't.
  const flexField = {
    'Existing Value':
      leftValue || rightValue ? `${leftValue}/${rightValue}` : '',
    Input: async (value, replyType) => {
      const [left, right] = String(value ?? '').split('/');
      setFieldListValue([leftField], left, replyType);
      setFieldListValue([rightField], right, replyType);
    }
  };

  if (description) {
    flexField.Description = description;
  }

  return flexField;
}

// DOMEvaluator receives the serializable question shape, while
// questionInteractions keeps the DOM element writers for PUSH_ANSWER.
function registerFlexQuestion(key, structure, originalElement) {
  if (!key || !structure || questionInteractions[key]) return;

  questionInteractions[key] = {
    Type: 'Flex',
    Question: key,
    Key: key,
    Structure: structure,
    OriginalElement: originalElement || document.body,
    Input: async (answer, replyType) => {
      return await answerFlexQuestion(key, answer, replyType);
    }
  };
}

function registerStructureFlexQuestion(
  fallbackSectionName,
  structure,
  firstField
) {
  if (Object.keys(structure).length === 0 || !firstField) {
    console.warn('Skipped Flex question: no fields found', {
      section: fallbackSectionName
    });
    return;
  }

  registerFlexQuestion(fallbackSectionName, structure, firstField);
}

// ---------------------------- DOM INTERACTION ----------------------------

function getFieldValue(field) {
  if (isEditableField(field)) return field.innerText || field.textContent || '';
  return field.value || '';
}

function shouldStripNonNumericCharacters(field) {
  const sectionName = getFreeTextSectionName(field);
  const subsectionName = getFreeTextSubsectionName(field, sectionName);
  const label = getFreeTextFieldLabel(field, subsectionName);
  const fieldIdentifier = `${field.id || ''} ${
    field.getAttribute('name') || ''
  }`;

  if (/comments/i.test(`${subsectionName} ${label} ${fieldIdentifier}`)) {
    return false;
  }

  return (
    NUMERIC_TEXT_FIELD_SECTIONS.has(sectionName) ||
    NUMERIC_TEXT_FIELD_UNIT_PATTERN.test(label)
  );
}

function normalizeValueForField(field, value) {
  let normalizedValue = String(value ?? '');

  if (field instanceof HTMLInputElement) {
    normalizedValue = normalizedValue
      .replace(/\s*\n+\s*/g, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  if (shouldStripNonNumericCharacters(field)) {
    normalizedValue = normalizedValue.replace(/[^0-9.]/g, '');
  }

  const maxLength = field.maxLength;
  if (maxLength > 0 && normalizedValue.length > maxLength) {
    console.warn('Truncated field value to maxlength', {
      field: getFieldLogInfo(field),
      maxLength
    });
    return normalizedValue.slice(0, maxLength);
  }

  return normalizedValue;
}

function setFieldValue(field, value, replyType = 'overwrite') {
  // Reply type comes from the selected template instructions.
  const existingValue = getFieldValue(field) || '';
  const valueToWrite =
    replyType === 'append'
      ? stripExistingPrefixForAppend(value, existingValue)
      : value;

  if (isEmptyOrNullorSameAsExisting(valueToWrite, existingValue)) {
    return;
  }

  const rawNextValue =
    replyType === 'append'
      ? `${existingValue}\n${valueToWrite}`.trim()
      : valueToWrite;
  const nextValue = normalizeValueForField(field, rawNextValue);

  if (isEmptyOrNullorSameAsExisting(nextValue, existingValue)) {
    return;
  }

  if (isEditableField(field)) {
    field.focus();
    field.textContent = nextValue;
  } else {
    field.focus();
    setNativeValue(field, nextValue);
  }

  triggerFieldEvents(field);
  markKinserFieldDirty(field);
}

function setNativeValue(field, value) {
  // Use the browser's native setter so framework-controlled fields still see
  // the value update consistently.
  let prototype = Object.getPrototypeOf(field);
  let valueSetter = null;

  while (prototype && !valueSetter) {
    valueSetter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
    prototype = Object.getPrototypeOf(prototype);
  }

  if (valueSetter) {
    try {
      valueSetter.call(field, value);
      return;
    } catch (error) {
      console.warn(
        'Native value setter failed; falling back to direct assignment',
        {
          field: getFieldLogInfo(field),
          error: error.message
        }
      );
    }
  }

  field.value = value;
}

function markKinserFieldDirty(field) {
  const fieldName = field.getAttribute('name');
  if (!fieldName) return;

  const dirtyFieldName = fieldName.replace(/\|0$/, '|1');
  if (dirtyFieldName === fieldName) return;

  field.setAttribute('name', dirtyFieldName);
}

function triggerFieldEvents(field) {
  // Kinser form controls use inline focus/change handlers to mark fields as
  // changed. Fire the common user-input events after setting the DOM value.
  field.dispatchEvent(new Event('focus', { bubbles: false }));
  field.dispatchEvent(new Event('focusin', { bubbles: true }));
  field.dispatchEvent(new Event('input', { bubbles: true }));
  if (!isRomStrengthGridField(field)) {
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }
  field.dispatchEvent(new Event('blur', { bubbles: true }));
}

function getPatientName() {
  const formHeaderPatientText = getFormHeaderPatientText();
  return formHeaderPatientText.replace(/\s*\([^)]*\)\s*$/, '').trim();
}

function getPatientEMRID() {
  const formHeaderPatientText = getFormHeaderPatientText();
  const formHeaderID = formHeaderPatientText?.match(/\(([^)]*)\)\s*$/)?.[1];
  return formHeaderID?.trim() || '';
}

function getFormHeaderPatientText() {
  const header = document.querySelector('#formHeader .leftCol');
  if (!header) return '';

  // The patient line is a text node next to the h2 note title:
  // <h2>PT Evaluation</h2> Patient, Test (1064)
  return Array.from(header.childNodes)
    .filter((node) => node.nodeType === Node.TEXT_NODE)
    .map((node) => node.textContent?.trim())
    .filter(Boolean)
    .join(' ')
    .trim();
}

function getFlexQuestions() {
  registerTextOnlyFlexQuestions();
}

function buildQuestionsFromInteractions() {
  return Object.keys(questionInteractions)
    .sort((a, b) => compareQuestionOrder(a, b))
    .map((key) => {
      const question = { ...questionInteractions[key] };
      delete question.OriginalElement;
      return question;
    });
}

function compareQuestionOrder(a, b) {
  const elementA = questionInteractions[a].OriginalElement;
  const elementB = questionInteractions[b].OriginalElement;

  if (!elementA || !elementB || elementA === elementB) return 0;

  return elementA.compareDocumentPosition(elementB) &
    Node.DOCUMENT_POSITION_FOLLOWING
    ? -1
    : 1;
}

function isFlexFieldEntry(node) {
  return isPlainObject(node) && typeof node.Input === 'function';
}

function isExistingValueAnswer(fieldDetails) {
  if (
    isPlainObject(fieldDetails) &&
    Object.prototype.hasOwnProperty.call(fieldDetails, 'Existing Value')
  ) {
    return true;
  }

  return false;
}

function getCompatibleAnswerValue(fieldDetails) {
  if (isExistingValueAnswer(fieldDetails)) {
    const existingValue = fieldDetails['Existing Value'];

    if (
      isPlainObject(existingValue) &&
      Object.prototype.hasOwnProperty.call(existingValue, 'Existing Value')
    ) {
      return existingValue['Existing Value'];
    }

    return existingValue;
  }

  return fieldDetails;
}

function findSubsectionFlexField(subsection, fieldKey) {
  const resolvedFieldKey = findFlexKey(subsection, getFlexFieldAlias(fieldKey));
  const matchingField = resolvedFieldKey ? subsection[resolvedFieldKey] : null;

  if (isFlexFieldEntry(matchingField)) {
    return {
      fieldKey: resolvedFieldKey,
      field: matchingField
    };
  }

  const fields = Object.entries(subsection).filter(([, field]) =>
    isFlexFieldEntry(field)
  );

  if (fields.length === 1) {
    return {
      fieldKey: fields[0][0],
      field: fields[0][1]
    };
  }

  return null;
}

function findOneLevelFlexField(structure, fieldKey) {
  const aliasedFieldKey = getFlexFieldAlias(fieldKey);

  for (const [subsectionKey, subsection] of Object.entries(structure)) {
    if (!isPlainObject(subsection) || isFlexFieldEntry(subsection)) continue;

    const resolvedFieldKey = findFlexKey(subsection, aliasedFieldKey);
    const field = resolvedFieldKey ? subsection[resolvedFieldKey] : null;

    if (isFlexFieldEntry(field)) {
      return {
        subsectionKey,
        fieldKey: resolvedFieldKey,
        field
      };
    }
  }

  return null;
}

async function applyFlexFieldAnswer(field, fieldDetails, replyType) {
  if (!isFlexFieldEntry(field)) {
    return 'Input function not found';
  }

  const value = getCompatibleAnswerValue(fieldDetails);

  try {
    await field.Input(value, replyType);
    return null;
  } catch (error) {
    return `Error applying value: ${error.message}`;
  }
}

// ---------------------------- EXPORTED INJECTORS ----------------------------

export function getPageInfo() {
  const baseURL = window.location.origin;
  const noteType = document
    .querySelector('#formHeader .leftCol h2')
    ?.textContent?.trim();

  return {
    URL: baseURL,
    'Note Type': noteType || DEFAULT_NOTE_TYPE
  };
}

export function getPatientInfo() {
  const patientName = getPatientName();
  const { firstName, lastName } = splitPatientName(patientName);
  // DOB is not visible on this page; keep the existing injector fallback for now.
  const dob = DEFAULT_DOB;
  const emrID =
    getPatientEMRID() || `${lastName},${firstName}`.replace(/\s/g, '');

  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: dob
  };
}

export function getQuestions() {
  questions = [];
  questionInteractions = {};

  getFlexQuestions();
  questions = buildQuestionsFromInteractions();

  return questions;
}

export async function answerQuestions(answers) {
  var errors = [];

  const answerPromises = answers.map(async (answer) => {
    var error = await answerQuestion(
      answer['Question'],
      answer['Answer'],
      answer['Reply Type']
    );
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });

  await Promise.all(answerPromises);

  if (errors.length > 0) console.error('Errors:', errors);
  return errors;
}

export async function answerQuestion(question, answer, replyType) {
  const interaction = questionInteractions[question];

  if (!interaction) {
    console.error(`Question not found: "${question}"`);
    return 'Question not found';
  }

  if (interaction.Input) {
    clearKinserAutoSaveTimer();
    activeKinserAnswerCount += 1;

    try {
      const error = await interaction.Input(answer, replyType);
      return error || null;
    } finally {
      activeKinserAnswerCount = Math.max(activeKinserAnswerCount - 1, 0);
      if (activeKinserAnswerCount === 0) {
        scheduleKinserAutoSave();
      }
    }
  }

  return 'Question type not supported';
}

function getButtonText(button) {
  if (button instanceof HTMLInputElement) {
    return button.value || button.alt || button.title || '';
  }
  return (
    button.textContent ||
    button.getAttribute('aria-label') ||
    button.getAttribute('title') ||
    ''
  );
}

function isSaveButton(button) {
  if (!isVisibleField(button)) return false;

  const onclick = button.getAttribute('onclick') || '';
  if (/^modifyForm\(['"]save['"]/.test(onclick.trim())) {
    return true;
  }

  return getButtonText(button).trim().toLowerCase().includes('save');
}

function findSaveButton() {
  return Array.from(
    document.querySelectorAll('input, button, a, [onclick], [role="button"]')
  ).find(isSaveButton) as HTMLElement | undefined;
}

// Click Save. We deliberately don't touch any dialog that may follow: a
// correctly-dated visit just saves (Kinser shows its own "saved" status), and
// the rare confirmation prompts (e.g. an out-of-work-week date) are left for
// the clinician to handle.
function saveNote() {
  const saveButton = findSaveButton();
  if (!saveButton) {
    console.warn('Kinser save button not found; skipping auto-save');
    return 'Save button not found';
  }

  saveButton.click();
  return null;
}

function clearKinserAutoSaveTimer() {
  if (kinserAutoSaveTimer) {
    window.clearTimeout(kinserAutoSaveTimer);
    kinserAutoSaveTimer = null;
  }
}

function scheduleKinserAutoSave() {
  clearKinserAutoSaveTimer();

  kinserAutoSaveTimer = window.setTimeout(() => {
    kinserAutoSaveTimer = null;

    try {
      const error = saveNote();
      if (error) {
        console.warn('Kinser auto-save failed:', error);
      }
    } catch (error) {
      console.error('Kinser auto-save failed:', error);
    }
  }, KINSER_AUTO_SAVE_DELAY_MS);
}

async function answerFlexQuestion(question, answer, replyType = 'overwrite') {
  try {
    const errors = [];
    const structure = questionInteractions[question]?.Structure || {};

    if (!isPlainObject(answer)) {
      console.warn(`Flex answer must be an object for "${question}".`);
      return 'Flex answer must be an object';
    }

    for (const [fieldKey, fieldDetails] of Object.entries(answer)) {
      const resolvedFieldKey = findFlexKey(
        structure,
        getFlexFieldAlias(fieldKey)
      );
      const fieldOrSubsection = resolvedFieldKey
        ? structure[resolvedFieldKey]
        : null;

      if (isFlexFieldEntry(fieldOrSubsection)) {
        const error = await applyFlexFieldAnswer(
          fieldOrSubsection,
          fieldDetails,
          replyType
        );
        if (error) errors.push({ Field: resolvedFieldKey, Error: error });
        continue;
      }

      if (isPlainObject(fieldOrSubsection)) {
        const subsectionField = findSubsectionFlexField(
          fieldOrSubsection,
          fieldKey
        );
        if (
          subsectionField &&
          (!isPlainObject(fieldDetails) || isExistingValueAnswer(fieldDetails))
        ) {
          const error = await applyFlexFieldAnswer(
            subsectionField.field,
            fieldDetails,
            replyType
          );
          if (error) {
            errors.push({
              Field: `${resolvedFieldKey}.${subsectionField.fieldKey}`,
              Error: error
            });
          }
          continue;
        }

        if (!isPlainObject(fieldDetails)) {
          errors.push({
            Field: resolvedFieldKey,
            Error: 'Flex subsection answer must be an object'
          });
          continue;
        }

        for (const [subFieldKey, value] of Object.entries(fieldDetails)) {
          const resolvedSubFieldKey = findFlexKey(
            fieldOrSubsection,
            getFlexFieldAlias(subFieldKey)
          );
          const field = resolvedSubFieldKey
            ? fieldOrSubsection[resolvedSubFieldKey]
            : null;

          const error = await applyFlexFieldAnswer(field, value, replyType);
          if (error) {
            errors.push({
              Field: `${resolvedFieldKey}.${subFieldKey}`,
              Error: error
            });
          }
        }
        continue;
      }

      const oneLevelField = findOneLevelFlexField(structure, fieldKey);
      if (oneLevelField) {
        const error = await applyFlexFieldAnswer(
          oneLevelField.field,
          fieldDetails,
          replyType
        );
        if (error) {
          errors.push({
            Field: `${oneLevelField.subsectionKey}.${oneLevelField.fieldKey}`,
            Error: error
          });
        }
        continue;
      }

      console.warn(`Field "${fieldKey}" not found in "${question}".`);
      errors.push({ Field: fieldKey, Error: 'Field not found in Structure' });
    }

    if (errors.length > 0) {
      console.error('Errors occurred:', errors);
      return errors;
    }

    return null;
  } catch (error) {
    console.error('Unexpected error:', error);
    return {
      Question: question,
      Error: `Unexpected error: ${error.message}`
    };
  }
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;

    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent: htmlContent }
    });
    return { htmlContent: htmlContent };
  } catch (error) {
    console.error('Error occurred while fetching document:', error);
    getLogFromInjector();
  }
}
